import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Loader from "@/designs/Loader";
import Modal from "@/designs/Modal";
import Select from "@/designs/Select";
import {
  Field,
  GetVistorsDocument,
  useCreateVisitorMutation,
  useGetCategoriesQuery,
  useGetOfficeLocationsQuery,
  User,
} from "@/graphql/generated/schema";
import { FileUp, X } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import EmployeeSearch from "./EmployeeSearch";
import { useTranslation } from "react-i18next";

interface AddNewVisit {
  isOpen: boolean;
  onClose: () => void;
}

const AddNewVisit = ({ isOpen, onClose }: AddNewVisit) => {
  const { theme, selectedLocation } = useContext(CMSModal);
  const { t } = useTranslation();

  const [localLocation, setLocalLocation] = useState<string | null>(null);
  const [hostType, setHostType] = useState<"none" | "employee">("none");
  const [formData, setFormData] = useState<Record<string, any>>({
    category: "",
    employee: "",
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [fileUploading, setFileUploading] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const { data: locationsData } = useGetOfficeLocationsQuery();
  const locationOptions =
    locationsData?.getOfficeLocations?.map((l) => ({
      label: l?.name || "",
      value: l?.id || "",
    })) || [];

  const { data, refetch } = useGetCategoriesQuery({
    variables: { location: localLocation! },
    fetchPolicy: "network-only",
    skip: !localLocation,
  });
  const category = data?.getCategories;
  const categories =
    category
      ?.filter((item) => item?.id && item?.name)
      .map((item) => ({
        label: item!.name as string,
        value: item!.id as string,
      })) || [];

  useEffect(() => {
    if (isOpen) {
      setLocalLocation(selectedLocation);
      if (selectedLocation) refetch({ location: selectedLocation });
    } else {
      setFormData({ category: "", employee: "" });
      setSelectedUser(null);
      setHostType("none");
      setLocalLocation(selectedLocation);
    }
  }, [isOpen, selectedLocation, refetch]);

  const [addVisitor, { loading: visitorLoading }] = useCreateVisitorMutation({
    refetchQueries: [GetVistorsDocument],
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (fieldName: string, file: File) => {
    const SERVER_URL = process.env.NEXT_PUBLIC_SERVER_IMG_URL;
    if (!SERVER_URL) return;
    setFileUploading((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const res = await fetch(`${SERVER_URL}/get-signed-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename: fileName, filetype: file.type }),
      });
      if (!res.ok) throw new Error();
      const { signedUrl, publicUrl } = await res.json();
      await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type, "x-amz-acl": "public-read" },
        body: file,
      });
      handleChange(fieldName, publicUrl);
    } catch {
      toast.error("File upload failed");
    } finally {
      setFileUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const selectedCategoryData = category?.find((item) => item?.id === formData.category);
  const enabledFields = (
    (selectedCategoryData?.fields?.filter(Boolean) as Field[]) || []
  ).filter((f: Field) => f.enabled !== false);

  const isFormValid = () => {
    if (!localLocation || !formData.category) return false;
    for (const field of enabledFields) {
      if (field.required && !formData[field.name || ""]) return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return alert("Please fill all required fields");
    try {
      const input = {
        category: formData.category || null,
        data: enabledFields.reduce((acc: Record<string, string>, field) => {
          acc[field.name || field.label || ""] =
            formData[field.name || field.label || ""] || "";
          return acc;
        }, {}),
        employee: hostType === "employee" ? formData.employee : null,
        location: localLocation!,
      };
      await addVisitor({ variables: { input } });
      onClose();
    } catch (err) {
      console.error("Error signing in visitor:", err);
    }
  };

  const categorySelected = !!formData.category;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("visitors.addVisit.title")}
    >
      {visitorLoading && <Loader />}
      <div className="space-y-5">
        {/* Location */}
        <Select
          options={locationOptions}
          value={localLocation || ""}
          required
          onChange={(value) => {
            setLocalLocation(value);
            handleChange("category", "");
          }}
          label="Location"
        />

        {/* Category */}
        <Select
          options={categories}
          value={formData.category}
          required
          onChange={(value) => {
            handleChange("category", value);
          }}
          label={t("visitors.addVisit.selectCategory")}
        />

        {/* Fields + Host — shown after category is selected */}
        {categorySelected && (
          <>
            {enabledFields.length > 0 && (
              <div className="space-y-4">
                {enabledFields.map((field: Field, index: number) => {
                  const value = formData[field.name || ""] || "";

                  if (field.type === "checkbox") {
                    return (
                      <div
                        key={index}
                        className="flex items-center space-x-2 border rounded-md px-3 py-2"
                      >
                        <input
                          type="checkbox"
                          id={field.name || ""}
                          required={field.required || false}
                          checked={!!value}
                          onChange={(e) =>
                            handleChange(
                              field.name || "",
                              e.target.checked ? "true" : "",
                            )
                          }
                          className="w-4 h-4 accent-primary cursor-pointer"
                        />
                        <label
                          htmlFor={field.name || ""}
                          className="text-sm font-medium"
                        >
                          {field.label}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                      </div>
                    );
                  }

                  if (field.type === "radio") {
                    const options = (field as any).options || [
                      { label: "Yes", value: "Yes" },
                      { label: "No", value: "No" },
                    ];
                    return (
                      <div key={index} className="flex flex-col px-3 py-2">
                        <label
                          className={twMerge(
                            "text-sm mb-1",
                            theme === "dark"
                              ? "text-gray-300"
                              : "text-gray-700",
                          )}
                        >
                          {field.label}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {options.map((opt: any, i: number) => (
                            <label
                              key={i}
                              className="flex items-center space-x-2"
                            >
                              <input
                                type="radio"
                                name={field.name || ""}
                                value={opt.value}
                                required={field.required || false}
                                checked={value === opt.value}
                                onChange={(e) =>
                                  handleChange(field.name || "", e.target.value)
                                }
                                className="w-4 h-4 accent-primary cursor-pointer"
                              />
                              <span className="text-sm">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (field.type === "phone") {
                    return (
                      <div key={index} className="flex flex-col space-y-1">
                        <label
                          className={twMerge(
                            "text-sm font-semibold",
                            theme === "dark" ? "text-gray-300" : "",
                          )}
                        >
                          {field.label}
                          {field.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        <PhoneInput
                          country={"us"}
                          value={formData[field.name || ""]}
                          onChange={(value, countryData: any) => {
                            const fullNumber = `+${countryData.dialCode}${value.replace(countryData.dialCode, "")}`;
                            handleChange(field.name || "", fullNumber);
                          }}
                          inputProps={{
                            name: field.name || "phone",
                            required: field.required || false,
                            autoFocus: false,
                          }}
                          containerClass="w-full"
                          inputClass={twMerge(
                            "!w-full !py-2 !pl-12 !border !rounded-md transition-colors",
                            theme === "dark"
                              ? "!bg-[#191919] !border-gray-700 !text-gray-100"
                              : "!bg-white !border-gray-300",
                          )}
                          buttonClass={twMerge(
                            "transition-colors",
                            theme === "dark"
                              ? "!bg-[#191919] !border-gray-700"
                              : "!bg-white !border-gray-300",
                          )}
                        />
                      </div>
                    );
                  }

                  if (field.type === "file") {
                    const isUploading = fileUploading[field.name || ""];
                    return (
                      <div key={index} className="flex flex-col space-y-1">
                        <label
                          className={twMerge(
                            "text-sm font-semibold",
                            theme === "dark" ? "text-gray-300" : "",
                          )}
                        >
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-0.5">*</span>
                          )}
                        </label>
                        {value ? (
                          <div
                            className={twMerge(
                              "flex items-center gap-2 border rounded-md px-3 py-2 transition-colors",
                              theme === "dark"
                                ? "border-gray-700 bg-[#191919]"
                                : "border-gray-300 bg-white",
                            )}
                          >
                            <a
                              href={value}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#099ed1] flex-1 truncate hover:underline"
                            >
                              {value.split("/").pop()}
                            </a>
                            <button
                              type="button"
                              onClick={() => {
                                handleChange(field.name || "", "");
                                const ref = fileInputRefs.current[field.name || ""];
                                if (ref) ref.value = "";
                              }}
                            >
                              <X size={14} className="text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        ) : (
                          <label
                            className={twMerge(
                              "flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer transition-colors",
                              theme === "dark"
                                ? "border-gray-700 bg-[#191919] text-gray-300 hover:bg-[#222]"
                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50",
                              isUploading ? "opacity-60 pointer-events-none" : "",
                            )}
                          >
                            <FileUp size={16} />
                            <span className="text-sm">
                              {isUploading ? "Uploading…" : "Choose file"}
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              required={field.required || false}
                              ref={(el) => {
                                fileInputRefs.current[field.name || ""] = el;
                              }}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleFileUpload(field.name || "", f);
                              }}
                            />
                          </label>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Input
                      key={index}
                      placeholder={field.label || ""}
                      label={field.label || ""}
                      value={value}
                      onChange={(value) =>
                        handleChange(field.name || "", value)
                      }
                      type={
                        (field.type || "text") as
                          | "time"
                          | "text"
                          | "date"
                          | undefined
                      }
                      required={field.required || false}
                    />
                  );
                })}
              </div>
            )}

            {/* Host */}
            <div className="space-y-2">
              <p
                className={twMerge(
                  "text-sm font-semibold",
                  theme === "dark" ? "text-gray-300" : "text-gray-700",
                )}
              >
                Host
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="hostType"
                  checked={hostType === "none"}
                  onChange={() => {
                    setHostType("none");
                    setSelectedUser(null);
                    handleChange("employee", "");
                  }}
                  className="w-4 h-4 accent-primary cursor-pointer"
                />
                <span
                  className={twMerge(
                    "text-sm",
                    theme === "dark" ? "text-gray-300" : "text-gray-700",
                  )}
                >
                  No host
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="hostType"
                  checked={hostType === "employee"}
                  onChange={() => setHostType("employee")}
                  className="w-4 h-4 accent-primary cursor-pointer"
                />
                <span
                  className={twMerge(
                    "text-sm",
                    theme === "dark" ? "text-gray-300" : "text-gray-700",
                  )}
                >
                  Visiting an Employee (host)
                </span>
              </label>

              {hostType === "employee" && (
                <div className="pt-1">
                  <EmployeeSearch
                    onSelect={(value) => {
                      setSelectedUser(value);
                      handleChange("employee", value.id || "");
                    }}
                    selectedValue={selectedUser}
                  />
                </div>
              )}
            </div>
          </>
        )}

        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || visitorLoading}
        >
          {visitorLoading
            ? t("visitors.addVisit.signingIn")
            : t("visitors.addVisit.signIn")}
        </Button>
      </div>
    </Modal>
  );
};

export default AddNewVisit;
